import React, { useState } from "react";
import axios from "axios";
import { contact } from "../mock";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    telefone: "",
    email: "",
    formacao: "",
    consent: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone || !form.email || !form.formacao) {
      toast({
        title: "Preenche todos os campos",
        description: "Por favor indica o teu nome, telefone, email e formação.",
      });
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailOk) {
      toast({
        title: "Email inválido",
        description: "Por favor indica um endereço de email válido.",
      });
      return;
    }
    if (!form.consent) {
      toast({
        title: "Consentimento necessário",
        description: "Precisamos da tua autorização para te contactar.",
      });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        nome: form.nome,
        telefone: form.telefone,
        email: form.email,
        formacao: form.formacao,
        consent: form.consent,
      });
      toast({
        title: "Pedido enviado com sucesso!",
        description: `Obrigado, ${form.nome}. A nossa equipa entrará em contacto brevemente.`,
      });
      setForm({ nome: "", telefone: "", email: "", formacao: "", consent: false });
    } catch (err) {
      toast({
        title: "Ocorreu um erro",
        description:
          "Não foi possível enviar o pedido. Tenta novamente dentro de momentos.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-[#d8d2cb] bg-white px-4 py-3 text-[#16204a] placeholder-[#9a9488] outline-none transition-all duration-200 focus:border-[#2f52c4] focus:ring-2 focus:ring-[#2f52c4]/20";

  return (
    <section id="contacto" className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left */}
          <div className="max-w-md">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#2f52c4]">
              {contact.label}
            </p>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold leading-tight text-[#16204a]">
              {contact.title}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#4a4f63]">
              {contact.description}
            </p>
            <p className="mt-4 text-sm text-[#8a8578]">{contact.note}</p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-[#e6e1da] bg-white p-6 md:p-8 shadow-sm"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#16204a]">
                  Nome
                </label>
                <input
                  type="text"
                  placeholder="Inserir nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#16204a]">
                  Telefone
                </label>
                <input
                  type="tel"
                  placeholder="Inserir telefone"
                  value={form.telefone}
                  onChange={(e) =>
                    setForm({ ...form, telefone: e.target.value })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#16204a]">
                Email
              </label>
              <input
                type="email"
                placeholder="Inserir email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputClass}
              />
            </div>

            <div className="mt-5">
              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-[#16204a]">
                Qual a formação que procuras?
              </label>
              <Select
                value={form.formacao}
                onValueChange={(v) => setForm({ ...form, formacao: v })}
              >
                <SelectTrigger className="w-full rounded-lg border border-[#d8d2cb] bg-white px-4 py-6 text-[#16204a]">
                  <SelectValue placeholder="Seleciona uma opção" />
                </SelectTrigger>
                <SelectContent>
                  {contact.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-6 flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={form.consent}
                onCheckedChange={(v) =>
                  setForm({ ...form, consent: Boolean(v) })
                }
                className="mt-0.5 border-[#c9c3ba] data-[state=checked]:bg-[#2f52c4] data-[state=checked]:border-[#2f52c4]"
              />
              <label
                htmlFor="consent"
                className="text-sm leading-relaxed text-[#4a4f63] cursor-pointer"
              >
                {contact.consent}
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full rounded-lg bg-[#16204a] py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:bg-[#0f1738] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "A enviar..." : contact.submit}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
